import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { format } from 'date-fns';
import { formatInstantInZone } from '../../../helpers/timezoneHelpers';
import { useSnapshot } from 'valtio';
import { toast } from 'react-toastify';
import { useQuery } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPaperPlane, faFlag } from '@fortawesome/free-solid-svg-icons';
import { doc, onSnapshot, Timestamp } from 'firebase/firestore';

import { authStore } from '../../../../store/authStore';
import chatService, { ChatMessage, Chat } from '../../../services/chatService/ChatService';
import firebaseService from '../../../services/firebase/FirebaseService';
import { GET_BOOKING_DETAILS } from '../../../services/bookingService/queries';
import { URLS } from '../../../../constants/urls';
import ReportUserModal from '../../../components/chat/ReportUserModal';
import { bookingDetailPath } from '../../../utils/productRoutes';
import { getSiteProductKind, isServicesSite } from '../../../constants/siteConfig';
import {
  useStayBookingAsGuestQuery,
  useStayBookingAsHostQuery,
} from '../../../services/stayService/__generated__/queries.generated';
import { toPublicS3ObjectUrl } from '../../../utils/s3PublicUrl';
import { stayDateRange, stayStatusView } from '../../StayBookingPage/stayBooking';
import { inquirySummary, isInquiry, isStayChat, stayChatTitle } from '../stayChat';

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/140x90?text=Car';

const getInitials = (name?: string) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0][0].toUpperCase();
};

const getAvatarColor = (name?: string) => {
  if (!name) return 'var(--color-black)';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 70%, 50%)`;
};

const Container = styled.div`
  ${tw`flex flex-col bg-white overflow-hidden`}
  height: calc(100dvh - 68px);
  min-height: calc(100vh - 68px);
`;

const ContentWrapper = styled.div`
  ${tw`w-full max-w-full min-w-0 max-w-screen-xl mx-auto flex flex-col flex-1 min-h-0 overflow-hidden box-border px-4 sm:px-6`}
`;

const Header = styled.div`
  ${tw`flex items-center px-4 py-3 bg-white border-b border-border-default`}
`;

const BackButton = styled.button`
  ${tw`p-2 mr-3 text-text-secondary`}
  &:hover {
    ${tw`text-text-primary`}
  }
`;

const HeaderInfo = styled.div`
  ${tw`flex-1`}
`;

const HeaderActions = styled.div`
  ${tw`flex items-center gap-2`}
`;

const ReportButton = styled.button`
  ${tw`p-2 text-text-secondary`}
  &:hover {
    ${tw`text-red-500`}
  }
`;

const HeaderTitle = styled.div`
  ${tw`text-lg font-bold text-text-primary`}
`;

const Avatar = styled.div<{ bgColor: string }>`
  ${tw`w-10 h-10 rounded-full mr-3 flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}
  background-color: ${props => props.bgColor};
`;

const HeaderSubtitle = styled.div`
  ${tw`text-xs text-text-secondary mt-1`}
`;

const BookingCard = styled.div`
  ${tw`m-4 bg-white rounded-lg p-4 flex items-center border border-border-default cursor-pointer transition-shadow flex-shrink-0`}
  &:hover {
    ${tw`shadow-card`}
  }
`;

const BookingImage = styled.img`
  ${tw`w-20 h-20 rounded-lg mr-4 object-cover bg-bg-secondary`}
`;

const BookingInfo = styled.div`
  ${tw`flex-1`}
`;

const CarTitle = styled.div`
  ${tw`text-base font-semibold text-text-primary`}
`;

const BookingDates = styled.div`
  ${tw`text-xs text-text-secondary mt-1`}
`;

const BookingStatus = styled.div`
  ${tw`text-xs text-text-secondary mt-2 font-semibold`}
`;

const MessagesContainer = styled.div`
  ${tw`flex-1 min-h-0 overflow-y-auto p-4 bg-bg-secondary`}
`;

const MessageBubble = styled.div<{ isCurrentUser: boolean }>`
  ${tw`mb-4 flex`}
  justify-content: ${props => (props.isCurrentUser ? 'flex-end' : 'flex-start')};
`;

const MessageContent = styled.div<{ isCurrentUser: boolean }>`
  ${tw`max-w-xs lg:max-w-md px-4 py-2 rounded-lg`}
  ${props =>
    props.isCurrentUser
      ? 'background-color: var(--color-text-primary); color: white;'
      : 'background-color: white; color: var(--color-text-primary); border: 1px solid var(--color-border);'}
`;

const MessageText = styled.div`
  ${tw`text-sm`}
`;

const MessageTime = styled.div<{ isCurrentUser: boolean }>`
  ${tw`text-xs mt-1`}
  color: ${props => (props.isCurrentUser ? 'var(--color-border)' : 'var(--color-text-secondary)')};
`;

const InputContainer = styled.div`
  ${tw`bg-white border-t border-border-default p-4 flex-shrink-0`}
`;

const InputWrapper = styled.div`
  ${tw`flex items-center`}
`;

const MessageInput = styled.input`
  ${tw`flex-1 px-4 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent`}
`;

const SendButton = styled.button<{ disabled: boolean }>`
  ${tw`ml-3 p-2 text-text-secondary disabled:text-text-tertiary disabled:cursor-not-allowed`}
  &:hover {
    ${tw`text-text-primary`}
  }
`;

const LoadingWrapper = styled.div`
  ${tw`flex justify-center items-center py-16`}
`;

const DeletedUserNotice = styled.div`
  ${tw`bg-yellow-50 border-l-4 border-yellow-400 p-4 m-4 rounded-md`}
`;

const DeletedUserText = styled.div`
  ${tw`text-sm text-yellow-800 italic`}
`;

const SeenLabel = styled.div`
  ${tw`text-xs text-text-tertiary mt-1 text-right`}
`;

const ChatDetailPage: React.FC = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useSnapshot(authStore);
  const currentUserId = auth.user?.id;
  const currentUserName =
    auth.user?.displayName || auth.user?.firstName || auth.user?.email || 'You';

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [chatData, setChatData] = useState<
    | (Pick<
        Chat,
        | 'kind'
        | 'bookingId'
        | 'listingId'
        | 'listingName'
        | 'listingImageUrl'
        | 'inquiry'
        | 'guestId'
        | 'hostId'
      > & {
        guestDeleted?: boolean;
        hostDeleted?: boolean;
        guestDisplayName?: string;
        hostDisplayName?: string;
        lastReadAtGuest?: Date | null;
        lastReadAtHost?: Date | null;
      })
    | null
  >(null);
  const [chatMissing, setChatMissing] = useState(false);
  // Spec 016 W27: a stay's chat (an inquiry, or a reservation) is described by its own
  // Firestore document; a car's chat is described by its booking, as before. The list passes
  // `stay` so the car booking query is not fired for a stay while the document loads.
  const stayChat = Boolean(location.state?.stay) || Boolean(chatData && isStayChat(chatData));
  // A chat opened without state (a link, a notification) learns its booking from the document.
  const bookingId = location.state?.bookingId || chatData?.bookingId || '';
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const hasMarkedAsReadRef = useRef(false);

  const { data: bookingData, loading: bookingLoading } = useQuery(GET_BOOKING_DETAILS, {
    variables: { id: bookingId },
    skip: !bookingId || stayChat,
  });

  const booking = bookingData?.booking;

  // The stay's dates and status for the header card of a reservation chat.
  const stayAsGuest = Boolean(chatData && chatData.guestId === currentUserId);
  const stayBookingSkip = !stayChat || !bookingId || !chatData;
  const stayGuestQuery = useStayBookingAsGuestQuery({
    variables: { id: bookingId },
    skip: stayBookingSkip || !stayAsGuest,
  });
  const stayHostQuery = useStayBookingAsHostQuery({
    variables: { id: bookingId },
    skip: stayBookingSkip || stayAsGuest,
  });
  const stayBooking =
    stayGuestQuery.data?.getBookingAsGuest ?? stayHostQuery.data?.getBookingAsHost;

  // Fetch chat data to check if user is deleted
  useEffect(() => {
    if (!chatId) return;

    const firestore = firebaseService.getFirestore();
    const chatRef = doc(firestore, 'chats', chatId);

    const unsubscribe = onSnapshot(
      chatRef,
      doc => {
        if (!doc.exists()) {
          setChatMissing(true);
          return;
        }
        if (doc.exists()) {
          const data = doc.data();
          setChatMissing(false);
          setChatData({
            kind: data?.kind === 'inquiry' || data?.kind === 'booking' ? data.kind : undefined,
            bookingId: data?.bookingId ?? undefined,
            listingId: data?.listingId ?? undefined,
            listingName: data?.listingName ?? undefined,
            listingImageUrl: data?.listingImageUrl ?? undefined,
            inquiry: data?.inquiry ?? undefined,
            guestId: data?.guestId,
            hostId: data?.hostId,
            guestDeleted: data?.guestDeleted ?? false,
            hostDeleted: data?.hostDeleted ?? false,
            guestDisplayName: data?.guestDisplayName,
            hostDisplayName: data?.hostDisplayName,
            lastReadAtGuest:
              data?.lastReadAt_guest instanceof Timestamp ? data.lastReadAt_guest.toDate() : null,
            lastReadAtHost:
              data?.lastReadAt_host instanceof Timestamp ? data.lastReadAt_host.toDate() : null,
          });
        }
      },
      error => {},
    );

    return () => unsubscribe();
  }, [chatId]);

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    if (!messagesContainerRef.current) return;
    messagesContainerRef.current.scrollTo({
      top: messagesContainerRef.current.scrollHeight,
      behavior,
    });
  };

  useEffect(() => {
    if (!chatId || !currentUserId) return;

    let unsubscribe: (() => void) | null = null;
    hasMarkedAsReadRef.current = false; // Reset when chatId changes

    const initializeChat = async () => {
      try {
        // Ensure Firebase authentication before subscribing
        await firebaseService.ensureAuthenticated();

        // Mark as read immediately when opening chat (only once per chatId)
        if (!hasMarkedAsReadRef.current) {
          chatService.markAsRead(chatId, currentUserId);
          hasMarkedAsReadRef.current = true;
        }

        unsubscribe = chatService.subscribeToMessages(
          chatId,
          currentUserId,
          (snapshotMessages: ChatMessage[]) => {
            setMessages(snapshotMessages);

            // Auto-mark as read only when user is actually on this chat detail page
            // Check both: page is visible AND current route matches this chatId
            const isPageVisible = document.visibilityState === 'visible';
            const isOnChatDetailPage = location.pathname === `/chat/${chatId}`;

            if (isPageVisible && isOnChatDetailPage) {
              chatService.markAsRead(chatId, currentUserId);
            }

            setTimeout(() => scrollToBottom('auto'), 100);
          },
        );
      } catch (error: any) {
        toast.error('Failed to load chat messages. Please ensure you are signed in.');
      }
    };

    initializeChat();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      hasMarkedAsReadRef.current = false; // Reset on unmount
    };
  }, [chatId, currentUserId, location.pathname]);

  useEffect(() => {
    scrollToBottom('auto');
  }, [messages.length]);

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!currentUserId || (!booking && !stayChat) || !messageText.trim()) {
      return;
    }
    const guestId = stayChat ? chatData?.guestId : booking?.guest?.id;
    const hostId = stayChat ? chatData?.hostId : booking?.host?.id;
    if (!guestId || !hostId) return;

    setSending(true);
    try {
      await chatService.sendMessage(
        chatId!,
        currentUserId,
        currentUserName,
        messageText.trim(),
        guestId,
        hostId,
      );
      setMessageText('');
    } catch (error: any) {
      // Check if error is from content moderation
      if (
        error?.message &&
        (error.message.includes('inappropriate content') ||
          error.message.includes('sensitive') ||
          error.blockedWords)
      ) {
        toast.error(
          'Message cannot be sent. It contains sensitive words. Please revise your message.',
          {
            autoClose: 4000,
          },
        );
      } else {
        toast.error(error?.message || 'Failed to send message. Please try again later.');
      }
    } finally {
      setSending(false);
    }
  };

  const currentUserIsGuest = stayChat
    ? chatData?.guestId === currentUserId
    : booking?.guest?.id === currentUserId;

  const bookingUrl = bookingDetailPath(
    currentUserIsGuest ? 'guest' : 'host',
    bookingId ?? '',
    // A worker's chat (spec 019 W12) uses the stay chat document, with job pages.
    stayChat && !isServicesSite() ? 'stay' : getSiteProductKind(),
  );

  const handleBookingCardPress = () => {
    if (bookingId) {
      navigate(bookingUrl);
    } else if (stayChat && chatData?.listingId) {
      // An inquiry: back to the place (or, on the services site, the worker) it is about.
      navigate(
        isServicesSite()
          ? `/services/workers/${chatData.listingId}`
          : `/stays/${chatData.listingId}`,
      );
    }
  };

  const otherParticipant = ():
    | {
        id?: string;
        displayName?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }
    | undefined => {
    if (!currentUserId) return undefined;
    if (stayChat) {
      if (!chatData) return undefined;
      return currentUserIsGuest
        ? { id: chatData.hostId, displayName: chatData.hostDisplayName }
        : { id: chatData.guestId, displayName: chatData.guestDisplayName };
    }
    if (!booking) return undefined;
    return booking.guest?.id === currentUserId ? booking.host : booking.guest;
  };

  const formatParticipantName = () => {
    const participant = otherParticipant();
    if (!participant) return 'Chat';

    // Check if participant is deleted
    const isGuest = currentUserIsGuest;
    const isDeleted = isGuest ? chatData?.hostDeleted : chatData?.guestDeleted;

    if (isDeleted) {
      // Use backup display name from chat data
      const backupName = isGuest ? chatData?.hostDisplayName : chatData?.guestDisplayName;
      return backupName || 'Deleted User';
    }

    // If displayName exists, use it
    if (participant.displayName) {
      return participant.displayName;
    }

    // If firstName and lastName exist, format them
    if (participant.firstName && participant.lastName) {
      return `${participant.firstName} ${participant.lastName}`;
    }

    // Fallback to email or 'Chat'
    return participant.email || 'Chat';
  };

  const isOtherParticipantDeleted = (): boolean => {
    if ((!booking && !stayChat) || !currentUserId || !chatData) return false;
    const isGuest = currentUserIsGuest;
    return (isGuest ? chatData.hostDeleted : chatData.guestDeleted) ?? false;
  };

  const relativeTime = (dt: Date): string => {
    const diffMs = Date.now() - dt.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const isCurrentUserGuest = currentUserIsGuest;
  const otherLastReadAt = isCurrentUserGuest ? chatData?.lastReadAtHost : chatData?.lastReadAtGuest;

  let lastSeenMsgId: string | null = null;
  if (otherLastReadAt) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.senderId === currentUserId && otherLastReadAt > msg.createdAt) {
        lastSeenMsgId = msg.id;
        break;
      }
    }
  }

  if (chatMissing) {
    return (
      <Container>
        <LoadingWrapper>
          <div>Chat not found</div>
        </LoadingWrapper>
      </Container>
    );
  }

  // A stay's chat waits for its document; a car's chat waits for its booking.
  if (bookingLoading || (!bookingId && !chatData)) {
    return (
      <Container>
        <LoadingWrapper>
          <div>Loading...</div>
        </LoadingWrapper>
      </Container>
    );
  }

  if (!booking && !stayChat) {
    return (
      <Container>
        <LoadingWrapper>
          <div>Booking not found</div>
        </LoadingWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <ContentWrapper style={{ flexDirection: 'row', alignItems: 'center', overflow: 'visible' }}>
          <BackButton onClick={() => navigate('/chat')}>
            <FontAwesomeIcon icon={faArrowLeft} size="lg" />
          </BackButton>
          <Avatar bgColor={getAvatarColor(formatParticipantName())}>
            {getInitials(formatParticipantName())}
          </Avatar>
          <HeaderInfo>
            <HeaderTitle>
              {stayChat && chatData ? stayChatTitle(chatData) : `Booking #${bookingId.slice(0, 8)}`}
            </HeaderTitle>
            <HeaderSubtitle>{formatParticipantName()}</HeaderSubtitle>
          </HeaderInfo>
          {otherParticipant() && !isOtherParticipantDeleted() && (
            <HeaderActions>
              <ReportButton onClick={() => setIsReportModalOpen(true)} title="Report User">
                <FontAwesomeIcon icon={faFlag} size="lg" />
              </ReportButton>
            </HeaderActions>
          )}
        </ContentWrapper>
      </Header>

      <ContentWrapper>
        {stayChat && chatData ? (
          <BookingCard onClick={handleBookingCardPress} data-testid="stay-chat-card">
            <BookingImage
              src={
                chatData.listingImageUrl
                  ? toPublicS3ObjectUrl(chatData.listingImageUrl)
                  : PLACEHOLDER_IMAGE
              }
              alt=""
            />
            <BookingInfo>
              <CarTitle>{stayChatTitle(chatData)}</CarTitle>
              <BookingDates data-testid="stay-chat-dates">
                {stayBooking
                  ? stayDateRange(
                      stayBooking.startAt,
                      stayBooking.endAt,
                      stayBooking.car?.location?.timezone,
                    )
                  : inquirySummary(chatData.inquiry) ||
                    (isInquiry(chatData) ? 'Dates not chosen yet' : '')}
              </BookingDates>
              <BookingStatus>
                {stayBooking
                  ? stayStatusView(stayBooking, currentUserIsGuest ? 'guest' : 'host').label
                  : isInquiry(chatData)
                    ? 'Inquiry · view the place'
                    : 'Reservation'}
              </BookingStatus>
            </BookingInfo>
          </BookingCard>
        ) : booking ? (
          <BookingCard onClick={handleBookingCardPress}>
            <BookingImage
              src={
                booking.car?.thumbnailUrl
                  ? `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${booking.car.thumbnailUrl}`
                  : PLACEHOLDER_IMAGE
              }
              alt="Car"
            />
            <BookingInfo>
              <CarTitle>
                {[booking.car?.make, booking.car?.model].filter(Boolean).join(' ') || 'Car'}
              </CarTitle>
              <BookingDates>
                {formatInstantInZone(
                  booking.startAt,
                  booking.car?.location?.timezone ?? 'America/Los_Angeles',
                  'MMM dd, yyyy',
                )}{' '}
                -{' '}
                {formatInstantInZone(
                  booking.endAt,
                  booking.car?.location?.timezone ?? 'America/Los_Angeles',
                  'MMM dd, yyyy',
                )}
              </BookingDates>
              <BookingStatus>{booking.status}</BookingStatus>
            </BookingInfo>
          </BookingCard>
        ) : null}

        <MessagesContainer ref={messagesContainerRef}>
          {messages.map(message => (
            <MessageBubble key={message.id} isCurrentUser={message.senderId === currentUserId}>
              <div>
                <MessageContent isCurrentUser={message.senderId === currentUserId}>
                  <MessageText>{message.text}</MessageText>
                  <MessageTime isCurrentUser={message.senderId === currentUserId}>
                    {format(message.createdAt, 'HH:mm')}
                  </MessageTime>
                </MessageContent>
                {message.senderId === currentUserId &&
                  lastSeenMsgId === message.id &&
                  otherLastReadAt && <SeenLabel>Seen {relativeTime(otherLastReadAt)}</SeenLabel>}
              </div>
            </MessageBubble>
          ))}
          <div ref={messagesEndRef} />
        </MessagesContainer>

        {isOtherParticipantDeleted() && (
          <DeletedUserNotice>
            <DeletedUserText>
              This user has deleted their account. You can view the chat history but cannot send new
              messages.
            </DeletedUserText>
          </DeletedUserNotice>
        )}

        <InputContainer>
          <form onSubmit={handleSendMessage}>
            <InputWrapper>
              <MessageInput
                type="text"
                placeholder={
                  isOtherParticipantDeleted()
                    ? 'Cannot send messages to deleted user'
                    : 'Type a message...'
                }
                value={messageText}
                onChange={e => setMessageText(e.target.value)}
                disabled={sending || isOtherParticipantDeleted()}
                style={{ opacity: isOtherParticipantDeleted() ? 0.5 : 1 }}
              />
              <SendButton
                type="submit"
                disabled={sending || !messageText.trim() || isOtherParticipantDeleted()}
                onClick={handleSendMessage}
              >
                <FontAwesomeIcon icon={faPaperPlane} size="lg" />
              </SendButton>
            </InputWrapper>
          </form>
        </InputContainer>
      </ContentWrapper>

      <ReportUserModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        reportedUserId={otherParticipant()?.id || ''}
        reportedUserName={formatParticipantName()}
        bookingId={bookingId}
        chatId={chatId}
      />
    </Container>
  );
};

export default ChatDetailPage;
